import { useEffect, useCallback, useMemo } from 'react';
import {
  useParams,
  useMatch,
  useNavigate,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import useUserData from 'approot/shared/user/user-data';
import {
  PUBLIC_LESSON_BASE,
  PUBLIC_ASSESSMENT_BASE,
  PUBLIC_UNIT_BASE,
  PUBLIC_TOPIC_BASE,
  PUBLIC_HOME,
  PUBLIC_SUBJECT_BASE,
} from 'global/public-routes.constants';
import {
  PRIVATE_CLASS_TO_DO,
  PRIVATE_PLANNER,
  PRIVATE_CREATE,
  PRIVATE_PLANNER_RECEIVE,
  PRIVATE_MY_STUDENTS,
} from 'global/private-routes.constants';
import { UserFragment_userClasses } from 'approot/shared/user/__generated__/UserFragment';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { setCurrentClass } from 'approot/classes/classes.apollo';
import { setContentFilterData } from 'approot/shared/filters/filters.apollo';
import { pushDataTrackerPageView } from 'approot/shared/data-tracker/data-tracker-push-pageview';
import { pushPageviewToDataLayer } from 'approot/shared/analytics/data-layer';
import { getActiveClasses } from 'approot/classes/classes.utils';
import { updateIntercomOnUrlChange } from 'lib/intercom/intercom';

const PAGE_WHITELIST = [
  `${PUBLIC_TOPIC_BASE}/`,
  `${PUBLIC_UNIT_BASE}/`,
  PUBLIC_LESSON_BASE,
  PUBLIC_ASSESSMENT_BASE,
  PRIVATE_CLASS_TO_DO,
  PRIVATE_PLANNER,
  PUBLIC_SUBJECT_BASE,
  PRIVATE_MY_STUDENTS,
];

function isOnWhitelistedPage(pathname: string) {
  if (
    pathname.startsWith(PRIVATE_CREATE) ||
    pathname.startsWith(PRIVATE_PLANNER_RECEIVE)
  ) {
    return false;
  }

  return (
    pathname === PUBLIC_HOME ||
    PAGE_WHITELIST.some(page => pathname.includes(page))
  );
}

export const useMultiClassNavigation = (triggerPageView: boolean = true) => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();
  const routeMatch = useMatch('/:userClassId/*');
  const { userClassId } = useParams<{ userClassId: string }>();

  const currentClass = useCurrentClassVar();
  const { isSignedIn, user } = useUserData();
  const userClasses = user?.userClasses;
  const classes = useMemo(() => getActiveClasses(userClasses || []), [
    userClasses,
  ]);

  const updateUserClass = useCallback(
    (userClass?: UserFragment_userClasses) => {
      setTimeout(() => setCurrentClass(userClass ? userClass : undefined));

      // add classId when missing in URL
      if (userClass && isOnWhitelistedPage(location.pathname) && !userClassId) {
        navigate(
          {
            pathname: `/${userClass.id}${location.pathname}`,
            search: location.search,
          },
          { replace: true }
        );
      }
      // update classId if not valid
      else if (userClass && userClassId) {
        navigate(
          {
            pathname: routeMatch?.pathname.replace(
              `/${userClassId}`,
              `/${userClass.id}`
            ),
            search: location.search,
          },
          { replace: true }
        );
      }
    },
    [
      location.pathname,
      location.search,
      userClassId,
      navigate,
      routeMatch?.pathname,
    ]
  );

  useEffect(() => {
    if (classes.length > 0 && isSignedIn && !currentClass) {
      let defaultClass = classes.find(c => c.isDefaultClass);
      // setup the class on load
      if (userClassId) {
        const userClass = classes.find(c => c.id === parseInt(userClassId));
        if (userClass) {
          setCurrentClass(userClass);
          defaultClass = userClass;
        } else {
          updateUserClass(defaultClass);
        }
      } else {
        updateUserClass(defaultClass);
      }

      // here we push 1 custom pageview
      pushPageviewToDataLayer();
      if (triggerPageView) pushDataTrackerPageView();
    } else if (!isSignedIn || currentClass) {
      // otherwise we already have a class or are not signed in
      if (triggerPageView) pushDataTrackerPageView();
    }

    updateIntercomOnUrlChange(
      user
        ? {
            id: user.id,
            email: user.email,
            hash: user.intercomHash,
            schoolId: user.school.id,
            schoolName: user.school.name,
            accountType: user.accountType,
          }
        : undefined
    );

    if (
      !userClassId &&
      currentClass &&
      classes &&
      isOnWhitelistedPage(location.pathname)
    ) {
      // the URL is out of sync with the class - might be from a blog link or email
      updateUserClass(classes.find(c => c.id === currentClass.id));
    }

    //eslint-disable-next-line
  }, [classes, isSignedIn]);

  // clean up userClassId on sign out
  useEffect(() => {
    if (!isSignedIn && routeMatch?.params.userClassId === userClassId) {
      navigate(
        {
          pathname: routeMatch?.pathname.replace(
            `/${routeMatch.params.userClassId}`,
            routeMatch.pathname.includes(`/${routeMatch.params.userClassId}/`)
              ? ''
              : PUBLIC_HOME
          ),
          search: location.search,
        },
        { replace: true }
      );
    }
    //eslint-disable-next-line
  }, [isSignedIn]);

  useEffect(() => {
    if (navigationType === 'POP' && isOnWhitelistedPage(location.pathname)) {
      const includesCurrentClassId = location.pathname.includes(
        `/${currentClass?.id}/`
      );
      if (!includesCurrentClassId && isSignedIn) {
        const idInUrl = location.pathname.split('/')[1];
        const newCurrentClass = classes?.find(c => c.id.toString() === idInUrl);
        setCurrentClass(newCurrentClass);
        setContentFilterData({
          yearFilter: newCurrentClass?.years.map(y => y.name) || [],
          curriculumFilter: newCurrentClass?.curriculums || [],
          subjectFilter: newCurrentClass?.subjects.map(s => s.name) || [],
          readPreferenceFilter: true,
          triggerContentRefresh: true,
        });
      }
    }
  }, [classes, currentClass, navigationType, isSignedIn, location.pathname]);

  return {
    classes,
  };
};
